/**
 * 📖 **웨일온 방 만드는 법** — 선생님·운영자 화면이 **같은 말**로 안내한다.
 *
 * ## 왜 문구를 여기 두나
 * 안내를 화면마다 적으면 갈린다. 실제로 갈려서 사고가 났다 — 한쪽엔
 * `on.whale.naver.com` 이라고 적혀 있었는데 **없는 도메인**이었다(2026-09-03).
 *
 * ## 🔴 여기 적힌 것은 **확인한 것만**이다
 * 단추 이름은 웨일온이 실제로 쓰는 말을 그대로 옮겼다(`전용회의실`·`호스트 없이 시작`·
 * `대기실 사용`). 지어낸 이름을 적으면 운영자가 없는 단추를 찾는다.
 *
 * 「어느 브라우저에서 되는가」도 이제는 **확인한 것**이다 — 아래
 * `WHALEON_BROWSER_SAY` 주석에 무엇을 어떻게 확인했는지 적어 두었다.
 */

/** 한 걸음씩 — 화면이 번호를 붙여 그린다. */
export const WHALEON_STEPS: readonly { title: string; body: string }[] = [
  {
    /**
     * 🔴 **주소가 중요하다.** `whaleon.naver.com` 첫 화면은 **회의에 들어가는 화면**이라
     * (「회의 입장」·「웨일 열기」 두 단추뿐) **로그인이 안 보인다.** 운영자가 실제로
     * 「접속했는데 로그인 위치를 모르겠어」라고 했다(2026-09-03). 방을 만드는 곳은 `/my` 다.
     */
    title: "whaleon.naver.com/my 로 들어간다 (첫 화면 말고 여기!)",
    body:
      "그냥 whaleon.naver.com 만 치면 「회의 입장」 화면이 떠서 로그인이 안 보여요. " +
      "주소 끝에 /my 를 붙이면 네이버 로그인이 나옵니다. " +
      "방을 만드는 사람만 계정이 필요해요 — 아이들·학부모는 계정 없이 링크로 들어옵니다.",
  },
  {
    title: "「나의 회의」 → 「전용회의실」 을 만든다",
    body:
      "전용회의실은 **한 번 만들면 주소가 안 바뀌는 방**이에요. 수업이 열두 주라면 " +
      "열두 번 만들 필요 없이 이 방 하나면 됩니다.",
  },
  {
    title: "「회의 옵션」에서 「비밀번호」를 걸고 「대기실 사용」을 켠다",
    body:
      "아이들이 얼굴을 켜고 있는 방이에요. 비밀번호가 있으면 주소만 알아도 못 들어오고, " +
      "대기실을 켜면 선생님이 확인하고 들여보낼 수 있어요. " +
      "「호스트 없이 시작」은 꺼 두세요 — 켜면 선생님 없이 아이들만 방에 있게 됩니다.",
  },
  {
    /**
     * 🔴 복사한 링크의 출처는 `whaleon.us` 다(`whaleon.naver.com` 이 아니다).
     * 운영자 화면에서 실제로 확인했다 — 그래서 `MEET_EMBED_ORIGINS` 에 둘 다 들어 있다.
     */
    title: "「회의 링크」를 복사한다 (「정보 복사」도 같아요)",
    body:
      "🔴 브라우저 **주소창의 주소가 아닙니다.** 방 안에서 복사한 링크여야 해요 — " +
      "주소창 주소를 넣으면 「유효하지 않은 회의 링크입니다」가 뜹니다. " +
      "복사한 링크는 whaleon.us 로 시작합니다(whaleon.naver.com 이 아니에요 — 맞는 거예요).",
  },
  {
    title: "여기에 붙여넣고 [🔎 확인] 을 누른다",
    body:
      "서버가 그 주소를 한 번 열어 보고 알려 드려요 — 없는 방인지, 우리 화면 안에서 " +
      "바로 열리는지. 좋으면 저장하세요.",
  },
] as const;

/**
 * 🐳 **PC 에서는 웨일 브라우저가 있어야 한다** — 확인했다(2026-09-03).
 *
 * ## 어떻게 확인했나
 * 운영자가 윈도우 크롬에서 진짜 초대 링크(`whaleon.us/o/…`)를 열고 화면을 보내 줬다.
 * 거기엔 **「웨일 열기」와 「다운로드 for Windows」 두 개뿐**이었다 — 「회의 입장」이 없다.
 * 그리고 「웨일 열기」를 눌러도 **아무 일도 안 일어났다**(웨일이 안 깔려 있으니 그렇다).
 *
 * 웨일온 화면 코드도 같은 말을 한다:
 *
 *     isWhale = (browser.name === "Whale")
 *
 * 즉 지금 브라우저가 웨일일 때만 그 자리에서 들어가는 길이 열린다.
 *
 * ## 🔴 전에 여기 적혀 있던 말은 **틀렸다**
 * 「[회의 입장]과 [웨일 열기] 두 단추가 나오는데 [회의 입장]으로 들어가지면 안 받아도 돼요」
 * — 웨일온 코드에 그 두 이름이 다 있길래 둘 다 보인다고 **넘겨짚은 것**이었다.
 * 실제로는 갈린다. 운영자는 그 말을 믿고 안 받았다가 「눌러도 아무것도 안 돼」를 겪었다.
 *
 * 그래서 안내를 **PC 는 받아야 한다**로 바꾸고, 받기 싫은 사람에게는 길을 준다 —
 * 회의 주소 칸은 어느 서비스냐를 안 따지므로 **구글밋·줌 주소를 넣으면 그대로 열린다**
 * (구글밋은 아무 브라우저에서나 설치 없이 열린다).
 */
export const WHALEON_BROWSER_SAY =
  "PC(윈도우·맥)에서는 웨일 브라우저를 받아야 해요. 크롬·엣지로 회의 링크를 열면 " +
  "[웨일 열기]와 [다운로드] 두 단추만 나오고, 웨일이 없으면 [웨일 열기]를 눌러도 " +
  "아무 일도 안 일어납니다(그 화면엔 「회의 입장」이 없어요). " +
  "받기 싫으시면 이 칸에 구글밋·줌 주소를 넣으셔도 돼요 — 그대로 열리고, " +
  "구글밋은 설치 없이 아무 브라우저에서나 열립니다.";

/** 웨일 브라우저 받는 곳 — 「받아야 한다」고 말했으면 어디서 받는지도 줘야 한다. */
export const WHALE_DOWNLOAD_URL = "https://whale.naver.com/ko/download/";

/** 웨일온 「내 회의」 화면 — 방을 만들고 로그인하는 곳. 루트(`/`)는 **입장 전용**이라 로그인이 안 보인다. */
export const WHALEON_MY_URL = "https://whaleon.naver.com/my";

/**
 * 🔎 **붙여넣은 것이 웨일온 「회의 링크」 모양인가.**
 *
 * 🔴 운영자가 「접속했는데 로그인 위치를 모르겠어」라고 했다. 루트 페이지는 **입장 전용**
 * (「회의 입장」·「웨일 열기」)이라 로그인이 안 보인다 — 앱은 `/my` 에 있다.
 * 그래서 **주소창의 `/my` 를 그대로 복사해 붙여넣기 쉽다.** 그건 회의 링크가 아니다.
 *
 * 웨일온 회의 링크는 이 모양이다(라우터에서 확인):
 *   `/o/<방번호>/<초대코드>` · `/w/<방번호>/<초대코드>` · `/on/<방번호>`
 *
 * 🔴 **출처가 둘이다.** 방을 만드는 곳은 `whaleon.naver.com` 인데 **복사되는 초대 링크는
 * `whaleon.us`** 다(운영자 화면에서 확인, 2026-09-03). `naver.com` 만 보고 있었더니
 * 진짜 링크를 붙여넣었을 때 여기서 **아무 말도 안 했고**, 더 나쁘게는 헤더가 그 출처에
 * 카메라를 안 넘겨서 **아이 화면에서 조용히 안 켜졌다.**
 *
 * 🔴 **모양이 아니어도 막지는 않는다.** 웨일온이 링크 모양을 바꾸는 날 멀쩡한 주소를
 * 거절하게 된다 — 「이건 회의 링크가 아닌 것 같아요」라고 **말만** 하고, 저장은 되게 둔다.
 */
const WHALEON_HOSTS = ["whaleon.naver.com", "whaleon.us"] as const;

export function whaleonLinkSay(url: string | undefined | null): string | null {
  const s = (url ?? "").trim();
  if (!s) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null; // 주소 모양이 아니면 다른 검사가 말한다
  }
  if (!(WHALEON_HOSTS as readonly string[]).includes(u.host)) return null; // 줌·구글밋은 여기서 안 본다
  if (/^\/(o|w)\/[^/]+\/[^/]+\/?$/.test(u.pathname) || /^\/on\/[^/]+\/?$/.test(u.pathname)) {
    return null; // 회의 링크 모양이다
  }
  if (u.pathname === "/" || u.pathname === "") {
    return "이건 웨일온 첫 화면 주소예요. 방에 들어가서 「회의 링크」를 복사해 주세요.";
  }
  if (u.pathname.startsWith("/my")) {
    return "이건 웨일온 **내 회의 목록** 주소예요(주소창 주소). 방을 열고 「회의 링크」를 복사해 주세요.";
  }
  return "웨일온 회의 링크 모양이 아닌 것 같아요. 방 안에서 「회의 링크」를 복사해 주세요.";
}
